import { SellerAnalyticsAreaChart } from 'components/PageWrappers/SharedComponents/SellerAnalyticsModal/components/SellerAnalyticsAreaChart';
import { SellerAnalyticsBarChart } from 'components/PageWrappers/SharedComponents/SellerAnalyticsModal/components/SellerAnalyticsBarChart';
import { SimpleDropdown } from 'components/Toolkit/SimpleDropdown/SimpleDropdown';
import { RadioTabs } from 'components/Toolkit/Inputs/RadioTabs';

import { PlotValue } from 'types';
import type { MenuItem } from 'components/Toolkit/Menu/Menu';
import type { ModalRequiredProps } from 'components/Toolkit/Modal/BaseModal';

import { useSellerAnalyticsModalDisplay } from 'components/PageWrappers/SharedComponents/SellerAnalyticsModal/hooks/useSellerAnalyticsModalDisplay/useSellerAnalyticsModalDisplay';

import * as SellerAnalyticsModalStyles from 'components/PageWrappers/SharedComponents/SellerAnalyticsModal/styles';

import type { ReactNode } from 'react';

import { formatNumber } from 'helpers/formatting';

import {
  SELLER_ANALYTICS_CATEGORIES,
  SELLER_ANALYTICS_TIME_RANGES,
  SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT,
  SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT,
} from 'types';

export interface ISellerAnalyticsModal extends ModalRequiredProps {
  baseUrl: string;
  cdnUrl: string;
  selectedTab?: SELLER_ANALYTICS_CATEGORIES;
  title?: string | ReactNode;
  sellerAnalyticsData: PlotValue[];
  fetchSellerAnalyticsData: (category: string, range: string) => void;
}

function SellerAnalyticsModal({
  isOpen,
  askToClose,
  title,
  selectedTab = SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
  sellerAnalyticsData,
  fetchSellerAnalyticsData,
}: ISellerAnalyticsModal) {
  const {
    selectedCategory,
    displayedTimeRange,
    total,
    mapCategoryValuesToDisplayText,
    setDisplayedTimeRange,
    handleChange,
  } = useSellerAnalyticsModalDisplay({
    selectedCategory: selectedTab,
    displayedTimeRange: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_7_DAYS,
    sellerAnalyticsData,
    fetchSellerAnalyticsData,
  });

  const Options: { [key: string]: MenuItem } = {
    SevenDays: {
      displayName: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_7_DAYS,
      onClick: () => {
        setDisplayedTimeRange(
          SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_7_DAYS,
        );
        fetchSellerAnalyticsData(
          selectedCategory,
          SELLER_ANALYTICS_TIME_RANGES.SEVEN_DAYS,
        );
      },
    },
    ThirtyDays: {
      displayName: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_30_DAYS,
      onClick: () => {
        setDisplayedTimeRange(
          SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_30_DAYS,
        );
        fetchSellerAnalyticsData(
          selectedCategory,
          SELLER_ANALYTICS_TIME_RANGES.THIRTY_DAYS,
        );
      },
    },
    NinetyDays: {
      displayName: SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_90_DAYS,
      onClick: () => {
        setDisplayedTimeRange(
          SELLER_ANALYTICS_TIME_RANGE_DISPLAY_TEXT.LAST_90_DAYS,
        );
        fetchSellerAnalyticsData(
          selectedCategory,
          SELLER_ANALYTICS_TIME_RANGES.NINETY_DAYS,
        );
      },
    },
  };

  function chartSelector(
    category: SELLER_ANALYTICS_CATEGORIES,
    data: PlotValue[],
  ) {
    switch (category) {
      case SELLER_ANALYTICS_CATEGORIES.AD_VIEWS:
        return (
          <SellerAnalyticsAreaChart
            data={data}
            labelText={mapCategoryValuesToDisplayText(selectedCategory)}
          />
        );
      case SELLER_ANALYTICS_CATEGORIES.SAVES:
      case SELLER_ANALYTICS_CATEGORIES.LEADS:
        return (
          <SellerAnalyticsBarChart
            data={data}
            labelText={mapCategoryValuesToDisplayText(selectedCategory)}
          />
        );
      default:
        return;
    }
  }

  return (
    <SellerAnalyticsModalStyles.SBaseModal
      isOpen={isOpen}
      askToClose={askToClose}
      testId="seller-analytics-modal"
      closeButtonCSS={SellerAnalyticsModalStyles.closeButtonCSS}
      closeTimeoutMS={400}
    >
      <SellerAnalyticsModalStyles.Header title={title} shouldTruncate />
      <RadioTabs
        name="seller-analytics"
        options={[
          {
            value: SELLER_ANALYTICS_CATEGORIES.AD_VIEWS,
            displayValue: SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.VIEWS,
          },
          {
            value: SELLER_ANALYTICS_CATEGORIES.SAVES,
            displayValue: SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.SAVES,
          },
          {
            value: SELLER_ANALYTICS_CATEGORIES.LEADS,
            displayValue: SELLER_ANALYTICS_CATEGORY_DISPLAY_TEXT.LEADS,
          },
        ]}
        value={selectedTab}
        onChange={handleChange}
      />
      <SellerAnalyticsModalStyles.SStatusBar
        status={
          <SellerAnalyticsModalStyles.SSpan>
            Total {mapCategoryValuesToDisplayText(selectedCategory)}:{' '}
            <b>{total > 0 ? formatNumber(total) : '--'}</b>
          </SellerAnalyticsModalStyles.SSpan>
        }
        linkText={
          <SimpleDropdown
            text={
              <SellerAnalyticsModalStyles.ManageButton>
                {displayedTimeRange}
              </SellerAnalyticsModalStyles.ManageButton>
            }
            variant="PRIMARY"
          >
            <SellerAnalyticsModalStyles.SMenu
              icons={false}
              items={[
                Options.SevenDays,
                Options.ThirtyDays,
                Options.NinetyDays,
              ]}
            />
          </SimpleDropdown>
        }
      />
      {sellerAnalyticsData ? (
        <SellerAnalyticsModalStyles.ChartContainer>
          {chartSelector(selectedCategory, sellerAnalyticsData)}
        </SellerAnalyticsModalStyles.ChartContainer>
      ) : (
        <SellerAnalyticsModalStyles.PlaceHolder>
          Nothing to show here at the moment.
          <br />
          Please come back tomorrow!
        </SellerAnalyticsModalStyles.PlaceHolder>
      )}
    </SellerAnalyticsModalStyles.SBaseModal>
  );
}

export { SellerAnalyticsModal };
